/**
 * gClient1Capability.ts — MER Algo Footprints — Phase 6 foundation (P1-Q4 / G-CLIENT-1)
 *
 * Pure client-side canonical-domain and advisory-capability semantics for
 * P1-Q4 position 8. Realizes exactly the banked authority D-GCLIENT1-1 through
 * D-GCLIENT1-5 (ADR section 48) in
 * docs/architecture-decisions/p1-q4-canonical-domain-capability-model.md, with
 * the governance record in docs/MASTER_TRANSFORMATION_PHASE_PLAN.md.
 *
 * PURE MODULE BOUNDARY. No React, no Zustand, no fetch, no timers, no clock, no
 * randomness, no provider logic, no symbol state, no actionability. Every export
 * is a constant or a deterministic function of its arguments.
 *
 * The canonical P1-Q4 timeframe vocabulary defined here is NOT the market-data
 * interval vocabulary. `DataState['interval']` remains a separate, broader,
 * active-symbol-scoped chart-fetch concern and is deliberately not reused here.
 *
 * `observedOperational` is absent by construction: position 8 neither derives,
 * stores, nor transmits it. Position 9 (G-CLIENT-4C) owns that predicate and the
 * shadow-observation dispatch under separately banked authority.
 */

// ── Banked canonical domain (bundled) ───────────────────────────────────────

/**
 * The exact P1-Q4 canonical client timeframe vocabulary — seven tokens, in
 * banked canonical order. Sub-minute market-data tokens are deliberately
 * excluded. Drift against the banked canonical-domain contract is proven
 * deterministically in the focused test, never by importing server sources at
 * browser runtime.
 */
export const CANONICAL_CLIENT_TIMEFRAMES = [
  '1min',
  '5min',
  '15min',
  '1h',
  '4h',
  '1D',
  '1W',
] as const;

export type CanonicalClientTimeframe = typeof CANONICAL_CLIENT_TIMEFRAMES[number];

/** The exact banked canonical horizon vocabulary, for advisory lookup only. */
export const CANONICAL_CLIENT_HORIZONS = [
  'scalp',
  'intraday',
  'swing',
  'position',
] as const;

export type CanonicalClientHorizon = typeof CANONICAL_CLIENT_HORIZONS[number];

/** First-use default only; never a substitution for an existing preference. */
export const DEFAULT_CANONICAL_TIMEFRAME: CanonicalClientTimeframe = '5min';

/** Identities the banked capability payload must declare to be usable. */
export const EXPECTED_CAPABILITY_CONTRACT_KIND = 'p1q4-capability-model';
export const EXPECTED_CAPABILITY_CONTRACT_VERSION = '1.0.0';
export const EXPECTED_DOMAIN_CONTRACT_KIND = 'p1q4-canonical-domain';
export const EXPECTED_DOMAIN_CONTRACT_VERSION = '1.0.0';

export const EXPECTED_CELL_COUNT =
  CANONICAL_CLIENT_HORIZONS.length * CANONICAL_CLIENT_TIMEFRAMES.length; // 28

// ── Advisory types ──────────────────────────────────────────────────────────

export interface CapabilityCellAdvisory {
  readonly staticState: string;
  readonly reasonCodes: readonly string[];
}

/**
 * The bounded advisory projection of a validated capability payload. It carries
 * no evidence, no compatibility, no runtime availability, and no operational
 * claim — only the static state and banked reason codes per canonical pair.
 */
export interface CapabilityOverlay {
  readonly contractVersion: string;
  readonly domainContractVersion: string;
  readonly cells: { readonly [pairKey: string]: CapabilityCellAdvisory };
}

/**
 * Current-fetch diagnostics. These are a separate channel from capability
 * reason codes: a transport or version failure is never rendered as, or
 * converted into, a banked cell reason.
 */
export type CapabilityDiagnosticKind =
  | 'TRANSPORT_FAILURE'
  | 'INVALID_PAYLOAD'
  | 'VERSION_MISMATCH'
  | 'DOMAIN_MISMATCH';

export interface CapabilityDiagnostic {
  readonly kind: CapabilityDiagnosticKind;
  /** Fixed, non-caller-derived detail describing which invariant failed. */
  readonly detail: string;
}

/** Overlay currency. A persisted overlay is never `CURRENT`. */
export type CapabilityOverlayStatus = 'CURRENT' | 'LAST_KNOWN' | 'NO_VALID_OVERLAY';

export interface SelectedPairAdvisory {
  readonly horizon: CanonicalClientHorizon;
  readonly timeframe: CanonicalClientTimeframe;
  readonly status: CapabilityOverlayStatus;
  /** Present only when an overlay exists and contains the canonical pair. */
  readonly cell: CapabilityCellAdvisory | null;
  readonly diagnostic: CapabilityDiagnostic | null;
}

export type CapabilityValidation =
  | { readonly ok: true; readonly overlay: CapabilityOverlay }
  | { readonly ok: false; readonly diagnostic: CapabilityDiagnostic };

// ── Local predicates ────────────────────────────────────────────────────────

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function invalid(kind: CapabilityDiagnosticKind, detail: string): CapabilityValidation {
  return { ok: false, diagnostic: { kind, detail } };
}

/** Stable canonical-pair key. Both operands come from fixed enums with no separator collision. */
export function pairKey(horizon: string, timeframe: string): string {
  return `${horizon}/${timeframe}`;
}

export function isCanonicalHorizon(value: unknown): value is CanonicalClientHorizon {
  return typeof value === 'string'
    && (CANONICAL_CLIENT_HORIZONS as readonly string[]).indexOf(value) !== -1;
}

export function isCanonicalTimeframe(value: unknown): value is CanonicalClientTimeframe {
  return typeof value === 'string'
    && (CANONICAL_CLIENT_TIMEFRAMES as readonly string[]).indexOf(value) !== -1;
}

// ── Fail-closed validation ──────────────────────────────────────────────────

/**
 * Validates a `GET /api/capabilities` response body against the banked
 * contract, fail-closed. Nothing is repaired, defaulted, normalized, or
 * fabricated: a payload either satisfies every invariant and yields a bounded
 * advisory overlay, or it yields a typed diagnostic and no overlay.
 */
export function validateCapabilityResponse(body: unknown): CapabilityValidation {
  if (!isPlainObject(body)) return invalid('INVALID_PAYLOAD', 'response body is not an object');
  if (body.status !== 'ok') return invalid('INVALID_PAYLOAD', 'response status is not ok');
  if (body.advisory !== true) return invalid('INVALID_PAYLOAD', 'response is not marked advisory');

  const model = body.capabilityModel;
  if (!isPlainObject(model)) return invalid('INVALID_PAYLOAD', 'capabilityModel is not an object');

  const metadata = model.metadata;
  if (!isPlainObject(metadata)) return invalid('INVALID_PAYLOAD', 'metadata is not an object');
  if (metadata.contractKind !== EXPECTED_CAPABILITY_CONTRACT_KIND) {
    return invalid('DOMAIN_MISMATCH', 'capability contract kind is not the banked kind');
  }
  if (metadata.contractVersion !== EXPECTED_CAPABILITY_CONTRACT_VERSION) {
    return invalid('VERSION_MISMATCH', 'capability contract version is not the expected version');
  }
  if (!isNonEmptyString(metadata.schemaVersion)) {
    return invalid('INVALID_PAYLOAD', 'schemaVersion is missing');
  }

  const payload = model.payload;
  if (!isPlainObject(payload)) return invalid('INVALID_PAYLOAD', 'payload is not an object');

  const domainContract = payload.domainContract;
  if (!isPlainObject(domainContract)) {
    return invalid('INVALID_PAYLOAD', 'domainContract is not an object');
  }
  if (domainContract.contractKind !== EXPECTED_DOMAIN_CONTRACT_KIND) {
    return invalid('DOMAIN_MISMATCH', 'linked domain contract kind is not the banked kind');
  }
  if (domainContract.contractVersion !== EXPECTED_DOMAIN_CONTRACT_VERSION) {
    return invalid('VERSION_MISMATCH', 'linked domain contract version is not the expected version');
  }

  const states = payload.states;
  if (!Array.isArray(states) || states.length === 0 || !states.every(isNonEmptyString)) {
    return invalid('INVALID_PAYLOAD', 'states is not a non-empty string array');
  }

  const cells = payload.cells;
  if (!Array.isArray(cells)) return invalid('INVALID_PAYLOAD', 'cells is not an array');
  if (cells.length !== EXPECTED_CELL_COUNT) {
    return invalid('INVALID_PAYLOAD', 'cells does not contain exactly the canonical cell count');
  }

  const collected: { [key: string]: CapabilityCellAdvisory } = {};
  for (const raw of cells) {
    if (!isPlainObject(raw)) return invalid('INVALID_PAYLOAD', 'a cell is not an object');
    if (!isCanonicalHorizon(raw.horizon) || !isCanonicalTimeframe(raw.timeframe)) {
      return invalid('DOMAIN_MISMATCH', 'a cell is not a canonical (horizon, timeframe) pair');
    }
    if (!isNonEmptyString(raw.staticState) || (states as string[]).indexOf(raw.staticState) === -1) {
      return invalid('INVALID_PAYLOAD', 'a cell staticState is not a declared state');
    }
    if (!Array.isArray(raw.reasonCodes) || !raw.reasonCodes.every(isNonEmptyString)) {
      return invalid('INVALID_PAYLOAD', 'a cell reasonCodes is not a string array');
    }
    const key = pairKey(raw.horizon, raw.timeframe);
    if (Object.prototype.hasOwnProperty.call(collected, key)) {
      return invalid('INVALID_PAYLOAD', 'a canonical cell is duplicated');
    }
    collected[key] = {
      staticState: raw.staticState,
      reasonCodes: (raw.reasonCodes as string[]).slice(),
    };
  }

  // Exact coverage: every canonical pair present, none missing.
  for (const horizon of CANONICAL_CLIENT_HORIZONS) {
    for (const timeframe of CANONICAL_CLIENT_TIMEFRAMES) {
      if (!Object.prototype.hasOwnProperty.call(collected, pairKey(horizon, timeframe))) {
        return invalid('INVALID_PAYLOAD', 'a canonical cell is missing');
      }
    }
  }

  return {
    ok: true,
    overlay: {
      contractVersion: metadata.contractVersion as string,
      domainContractVersion: domainContract.contractVersion as string,
      cells: collected,
    },
  };
}

// ── Advisory derivation ─────────────────────────────────────────────────────

/**
 * Overlay currency. A persisted overlay rehydrated before any successful
 * current fetch is `LAST_KNOWN`, never `CURRENT`.
 */
export function resolveOverlayStatus(
  current: CapabilityOverlay | null,
  lastKnown: CapabilityOverlay | null,
): CapabilityOverlayStatus {
  if (current) return 'CURRENT';
  if (lastKnown) return 'LAST_KNOWN';
  return 'NO_VALID_OVERLAY';
}

/**
 * Advisory state for the canonical selected pair. Looks up nothing symbol-
 * scoped and consults no market-data interval. The diagnostic travels alongside
 * the cell rather than being folded into its reason codes.
 */
export function selectPairAdvisory(input: {
  horizon: CanonicalClientHorizon;
  timeframe: CanonicalClientTimeframe;
  current: CapabilityOverlay | null;
  lastKnown: CapabilityOverlay | null;
  diagnostic: CapabilityDiagnostic | null;
}): SelectedPairAdvisory {
  const status = resolveOverlayStatus(input.current, input.lastKnown);
  const overlay = input.current || input.lastKnown;
  const key = pairKey(input.horizon, input.timeframe);
  const cell = overlay && Object.prototype.hasOwnProperty.call(overlay.cells, key)
    ? overlay.cells[key]
    : null;

  return {
    horizon: input.horizon,
    timeframe: input.timeframe,
    status,
    cell,
    diagnostic: input.diagnostic,
  };
}

/**
 * First-use resolution. An existing canonical preference is returned unchanged;
 * the default applies only to a genuinely absent or non-canonical stored value,
 * and never as a substitution triggered by capability or fetch conditions.
 */
export function resolveInitialCanonicalTimeframe(
  persisted: unknown,
): CanonicalClientTimeframe {
  return isCanonicalTimeframe(persisted) ? persisted : DEFAULT_CANONICAL_TIMEFRAME;
}
